import { lazy, Suspense } from 'react';

import { authActions } from '@store/auth/auth.slice';
import { Menu, MenuLink } from '@style/global-style.app';

import ConfigurationIcon from '@asset/configuration.icon.svg?react';
import ConsumerIcon from '@asset/consumer.icon.svg?react';
import CredentialIcon from '@asset/credential.icon.svg?react';
import DashboardIcon from '@asset/dashboard.icon.svg?react';
import LogsIcon from '@asset/logs.icon.svg?react';
import MoreIcon from '@asset/more.icon.svg?react';
import OverviewIcon from '@asset/overview.icon.svg?react';
// import SequencesIcon from '@asset/sequences.icon.svg?react';
import SettingsIcon from '@asset/settings.icon.svg?react';
import UserIcon from '@asset/user.icon.svg?react';
import WebhooksIcon from '@asset/webhooks.icon.svg?react';
import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Text } from '@atom/typography/typography';
import { UserName } from '@component/aside/navigation/user-name/user-name';
import { RouteEnum } from '@enum/route.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { useStoreDispatch, useStoreSelector } from '@hook/store/store.hooks';

import { subPageRouteMenu } from './navigation.aside-options';
import { ButtonWrapper, Divider, DocsWrapper } from './navigation.aside-style';

const CurrentApplication = lazy(() => import('./current-application/current-application'));

interface NavigationAsideProps {
  hasNavigationApplication?: boolean;
}

export const NavigationAside = ({ hasNavigationApplication = true }: NavigationAsideProps) => {
  const dispatch = useStoreDispatch();
  const routeName = useStoreSelector((state) => state.route.name);

  const handleLogout = () => dispatch(authActions.logout());

  return (
    <Suspense>
      <CurrentApplication />
      <Divider />
      {hasNavigationApplication && (
        <div id='dashboard-4'>
          <Menu
            selectedKeys={subPageRouteMenu[routeName] ?? [routeName]}
            items={[
              {
                key: RouteEnum.Dashboard.name,
                icon: <DashboardIcon />,
                label: <MenuLink to={RouteEnum.Dashboard.path}>Dashboard</MenuLink>,
              },
              {
                key: RouteEnum.Credentials.name,
                icon: <CredentialIcon />,
                label: <MenuLink to={RouteEnum.Credentials.path}>Credentials</MenuLink>,
              },
              {
                key: RouteEnum.Integrations.name,
                icon: <ConfigurationIcon />,
                label: <MenuLink to={RouteEnum.Integrations.path}>Integrations</MenuLink>,
              },
              {
                key: RouteEnum.Consumers.name,
                icon: <ConsumerIcon />,
                label: <MenuLink to={RouteEnum.Consumers.path}>Consumers</MenuLink>,
              },
              {
                key: RouteEnum.Webhooks.name,
                icon: <WebhooksIcon />,
                label: <MenuLink to={RouteEnum.Webhooks.path}>Webhooks</MenuLink>,
              },
              // {
              //   key: RouteEnum.Sequences.name,
              //   icon: <SequencesIcon />,
              //   label: <MenuLink to={RouteEnum.Sequences.path}>Sequences</MenuLink>,
              // },
              {
                key: RouteEnum.Logs.name,
                icon: <LogsIcon />,
                label: <MenuLink to={RouteEnum.Logs.path}>Logs</MenuLink>,
              },
            ]}
          />
          <Divider />
        </div>
      )}
      <Menu
        selectedKeys={subPageRouteMenu[routeName] ?? [routeName]}
        items={[
          {
            key: RouteEnum.Docs.name,
            icon: <OverviewIcon />,
            label: (
              <MenuLink rel='noopener noreferrer' target='_blank' to={import.meta.env.VITE_SOURCE_DOCS}>
                <DocsWrapper>
                  <Text color={ColorEnum.Inherit}>Docs</Text>
                  <MoreIcon width={16} />
                </DocsWrapper>
              </MenuLink>
            ),
          },
          {
            key: RouteEnum.Explorer.name,
            icon: <OverviewIcon />,
            label: (
              <MenuLink rel='noopener noreferrer' target='_blank' to={RouteEnum.Explorer.path}>
                <DocsWrapper>
                  <Text color={ColorEnum.Inherit}>Explorer</Text>
                  <MoreIcon width={16} />
                </DocsWrapper>
              </MenuLink>
            ),
          },
        ]}
      />
      <Divider />
      <div id='dashboard-5'>
        <Menu
          selectedKeys={subPageRouteMenu[routeName] ?? [routeName]}
          items={[
            {
              key: RouteEnum.Settings.name,
              icon: <UserIcon />,
              label: (
                <MenuLink to={RouteEnum.Settings.path}>
                  <UserName />
                </MenuLink>
              ),
            },
            {
              key: RouteEnum.AccountSettings.name,
              icon: <SettingsIcon />,
              label: <MenuLink to={RouteEnum.AccountSettings.path}>Account Settings</MenuLink>,
            },
          ]}
        />
      </div>
      <Divider />
      <ButtonWrapper>
        <Button type={ButtonTypeEnum.Primary} ghost onClick={handleLogout}>
          <Text color={ColorEnum.White} letterSpacing={1.4} level='m' weight={500}>
            LOG OUT
          </Text>
        </Button>
      </ButtonWrapper>
    </Suspense>
  );
};
